import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { StaffInbox } from "../components/StaffInbox";
import { api, money, STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import { Receipt, Plus, ArrowUpRight, Package, Clock, TrendUp, CalendarCheck, Phone, Bell } from "@phosphor-icons/react";
import NewTicketWizard from "../components/NewTicketWizard";
import { prefetchWizardCatalog } from "../lib/catalogCache";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  // Track previously-seen new-booking count so we can chime when a fresh
  // one arrives. Persisted in localStorage so a hard refresh doesn't
  // re-trigger the alert for already-seen bookings.
  const lastSeenRef = useRef(
    Number(localStorage.getItem("dashboard_new_bookings_seen") || 0)
  );

  const loadStats = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; // e.g. "Australia/Brisbane"
    return api.get("/dashboard/stats", { params: { tz } }).then((r) => {
      setStats(r.data);
      // Audible chime + native browser notification when the new-bookings
      // count rises above the last seen value. Only fires once per
      // increase — not on every poll.
      const fresh = Number(r.data?.new_bookings_count || 0);
      if (fresh > lastSeenRef.current) {
        try {
          // Short web-audio beep so staff hear it across the shop floor.
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.type = "sine"; o.frequency.value = 880;
          g.gain.setValueAtTime(0.0001, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
          o.start(); o.stop(ctx.currentTime + 0.42);
        } catch { /* ignore — autoplay policy may block until user interacts */ }
        if ("Notification" in window && Notification.permission === "granted") {
          try {
            new Notification("New booking received", {
              body: `${fresh - lastSeenRef.current} fresh booking${fresh - lastSeenRef.current === 1 ? "" : "s"} need attention`,
              icon: "/favicon.ico",
            });
          } catch { /* ignore */ }
        }
      }
      lastSeenRef.current = fresh;
    });
  };

  useEffect(() => {
    loadStats();
    prefetchWizardCatalog().catch(() => {});
    // Re-poll every 30s so fresh website bookings surface without a refresh.
    const t = setInterval(loadStats, 30000);
    // Ask for browser notification permission once. Silent if denied.
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return () => clearInterval(t);
  }, []);

  const acknowledgeNewBookings = () => {
    // Mark all currently-known new bookings as seen so the alert dismisses
    // until a freshly-fresh one arrives.
    const fresh = Number(stats?.new_bookings_count || 0);
    lastSeenRef.current = fresh;
    localStorage.setItem("dashboard_new_bookings_seen", String(fresh));
  };

  const revenue = stats?.today_revenue ?? 0;
  const salesCount = stats?.today_sales_count ?? 0;
  const avgTicket = stats?.avg_ticket_value ?? 0;
  const closedToday = stats?.today_tickets_closed ?? 0;
  const openTickets = stats?.open_tickets ?? 0;
  const totalCustomers = stats?.total_customers ?? 0;

  return (
    <Layout>
      <PageHeader
        overline="Overview"
        title="Today at the counter"
        actions={
          <>
            <button
              onMouseEnter={() => prefetchWizardCatalog().catch(() => {})}
              onFocus={() => prefetchWizardCatalog().catch(() => {})}
              onClick={() => {
                prefetchWizardCatalog().catch(() => {});
                setWizardOpen(true);
              }}
              data-testid="dashboard-new-ticket"
              className="h-11 px-5 rounded-full bg-white border border-[#E5E7EB] text-[13px] font-medium flex items-center gap-2 hover:border-[#121216] hover:text-[#121216] text-[#4B5563] transition-all active:scale-[0.98]"
            >
              <Plus size={14} weight="bold" /> New ticket
            </button>
            <Link
              to="/pos"
              data-testid="dashboard-new-sale"
              className="h-11 px-5 rounded-full bg-[#121216] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1E5FEB] transition-all active:scale-[0.98]"
            >
              <Receipt size={14} weight="bold" /> New sale
            </Link>
          </>
        }
      />

      <div className="px-4 sm:px-6 md:px-8 pb-10 pt-2 space-y-6 sm:space-y-8">
        <StaffInbox />
        {/* ============ NEW BOOKINGS ALERT ============ */}
        {/* Prominent banner shown whenever there are status='new' bookings
            for this store. Polls every 30s; chimes + flashes when a fresh
            one arrives. Click "Acknowledge" to dismiss until next arrival. */}
        {(stats?.new_bookings_count || 0) > 0 && (
          <section
            data-testid="new-bookings-alert"
            className="relative overflow-hidden rounded-[20px] border-2 border-[#1E5FEB] bg-gradient-to-r from-[#EBF2FF] via-[#F0F6FF] to-[#EBF2FF]"
            style={{ animation: "pulse-blue 2s ease-in-out infinite", boxShadow: "0 0 0 0 rgba(30, 95, 235, 0.4), 0 8px 32px rgba(30, 95, 235, 0.18)" }}
          >
            <style>{`
              @keyframes pulse-blue {
                0%, 100% { box-shadow: 0 0 0 0 rgba(30, 95, 235, 0.0), 0 8px 32px rgba(30, 95, 235, 0.18); }
                50%      { box-shadow: 0 0 0 6px rgba(30, 95, 235, 0.18), 0 8px 32px rgba(30, 95, 235, 0.28); }
              }
            `}</style>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-6 p-4 sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#1E5FEB] flex items-center justify-center shrink-0 shadow-md shadow-blue-500/30">
                  <Bell size={20} weight="fill" className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#1E5FEB] font-bold">
                    New bookings need attention
                  </div>
                  <div className="text-[16px] sm:text-[20px] font-semibold text-[#121216] mt-0.5 truncate">
                    {stats.new_bookings_count} booking{stats.new_bookings_count === 1 ? "" : "s"} from the website
                  </div>
                  {(stats.new_bookings || []).slice(0, 1).map((b) => (
                    <div key={b.id} className="text-[12px] text-[#4B5563] mt-1 truncate">
                      Latest: <span className="font-medium text-[#121216]">{b.customer_name}</span>
                      {b.device_make ? ` · ${b.device_make} ${b.device_model || ""}` : ""}
                      {b.device_year ? ` (${b.device_year})` : ""}
                      {b.device_model_number ? ` · ${b.device_model_number}` : ""}
                      {b.problem ? ` · ${b.problem}` : ""}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={acknowledgeNewBookings}
                  data-testid="dashboard-ack-bookings"
                  className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-[12px] font-medium text-[#4B5563] hover:border-[#121216] hover:text-[#121216] transition-all"
                >
                  Acknowledge
                </button>
                <Link
                  to="/bookings?status=new"
                  data-testid="dashboard-view-bookings"
                  className="h-10 px-5 rounded-full bg-[#1E5FEB] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1849C8] transition-all active:scale-[0.98]"
                >
                  Review now <ArrowUpRight size={13} weight="bold" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ============ HERO SNAPSHOT ============ */}
        <section
          data-testid="today-snapshot"
          className="relative overflow-hidden rounded-[28px] border border-[#E5E7EB] bg-white"
          style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02), 0 8px 32px rgba(30, 95, 235, 0.06)" }}
        >
          {/* Soft glass gradient glow */}
          <div
            aria-hidden
            className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
            style={{
              background: "radial-gradient(circle, rgba(30, 95, 235, 0.18) 0%, rgba(30, 95, 235, 0) 60%)",
              filter: "blur(20px)",
            }}
          />
          <div className="relative grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr]">
            {/* Revenue hero */}
            <div className="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-1 p-6 sm:p-8 lg:p-10 border-b border-[#F3F4F6] lg:border-b-0 lg:border-r">
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-5">
                Today's revenue
              </div>
              <div
                className="font-display text-[40px] sm:text-[52px] lg:text-[64px] leading-[1] tracking-[-0.03em] font-semibold text-[#121216]"
                data-testid="kpi-revenue"
              >
                <span className="text-[#9CA3AF] text-[40px] font-medium mr-1 align-top">$</span>
                {money(revenue)}
              </div>
              <div className="mt-6 flex items-center gap-2 text-[12px] text-[#4B5563]">
                <TrendUp size={14} weight="bold" className="text-[#1E5FEB]" />
                <span className="font-medium">{salesCount}</span>
                <span className="text-[#9CA3AF]">sales · avg</span>
                <span className="font-medium">${money(avgTicket)}</span>
              </div>
            </div>

            {/* Sub-stats */}
            <SnapStat label="Sales" value={salesCount} testid="kpi-sales" />
            <SnapStat
              label="Tickets closed"
              value={closedToday}
              testid="kpi-closed"
              divider
            />
            <SnapStat
              label="Open tickets"
              value={openTickets}
              testid="kpi-tickets"
              divider
              trailing={
                <Link
                  to="/repairs"
                  className="text-[11px] font-medium text-[#1E5FEB] hover:text-[#1849C8] inline-flex items-center gap-1"
                >
                  View all <ArrowUpRight size={12} weight="bold" />
                </Link>
              }
            />
            <SnapStat
              label="Customers"
              value={totalCustomers.toLocaleString()}
              testid="kpi-customers"
              divider
              trailing={
                <Link
                  to="/customers"
                  className="text-[11px] font-medium text-[#1E5FEB] hover:text-[#1849C8] inline-flex items-center gap-1"
                >
                  Address book <ArrowUpRight size={12} weight="bold" />
                </Link>
              }
            />
          </div>
        </section>

        {/* ============ MAIN GRID ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 sm:gap-6">
          {/* Action needed */}
          <section
            className="rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden"
            style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02)" }}
          >
            <header className="px-7 pt-7 pb-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
                  Action needed
                </div>
                <div className="mt-1 text-[18px] font-display font-semibold text-[#121216]">
                  Ready & awaiting parts
                </div>
              </div>
              <Link
                to="/repairs"
                className="text-[12px] text-[#4B5563] hover:text-[#121216] inline-flex items-center gap-1"
              >
                Workshop queue <ArrowUpRight size={12} weight="bold" />
              </Link>
            </header>

            <div className="px-3 pb-3">
              {(stats?.action_needed_tickets || []).length === 0 ? (
                <div className="mx-4 mb-4 px-5 py-10 rounded-2xl bg-[#F8FAFC] text-center">
                  <div className="text-sm text-[#4B5563]">No tickets need attention right now.</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1">Ready-for-pickup & waiting-parts tickets will show here.</div>
                </div>
              ) : (
                <ul className="divide-y divide-[#F3F4F6]">
                  {(stats?.action_needed_tickets || []).map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/repairs/${t.id}`}
                        className="group flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-[#F8FAFC] transition-colors"
                        data-testid={`action-ticket-${t.ticket_number}`}
                      >
                        <StatusDot status={t.status} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[11px] text-[#9CA3AF]">{t.ticket_number}</span>
                            <span className="text-[14px] font-semibold text-[#121216] truncate">
                              {t.customer_name}
                            </span>
                          </div>
                          <div className="text-[12px] text-[#4B5563] truncate mt-0.5">
                            {t.device_make} {t.device_model} — {t.issue}
                          </div>
                        </div>
                        <StatusPill status={t.status} />
                        <ArrowUpRight
                          size={14}
                          weight="bold"
                          className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Low stock */}
          <section
            className="rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden"
            style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02)" }}
          >
            <header className="px-7 pt-7 pb-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
                  Low stock
                </div>
                <div className="mt-1 text-[18px] font-display font-semibold text-[#121216]">
                  {stats?.low_stock_count ?? 0} items
                </div>
              </div>
              <Package size={18} weight="duotone" className="text-[#9CA3AF]" />
            </header>

            <div className="px-3 pb-3">
              {(stats?.low_stock_items || []).length === 0 ? (
                <div className="mx-4 mb-4 px-5 py-8 rounded-2xl bg-[#F8FAFC] text-center text-sm text-[#4B5563]">
                  Inventory healthy.
                </div>
              ) : (
                <ul className="divide-y divide-[#F3F4F6]">
                  {(stats?.low_stock_items || []).map((p) => (
                    <li key={p.id} className="px-4 py-3 flex items-center justify-between" data-testid={`low-stock-${p.sku}`}>
                      <div className="min-w-0 pr-3">
                        <div className="text-[13px] font-semibold text-[#121216] truncate">{p.name}</div>
                        <div className="text-[10px] font-mono text-[#9CA3AF] mt-0.5">{p.sku}</div>
                      </div>
                      <span className="font-display text-[22px] font-semibold text-[#DC2626] tabular-nums">
                        {p.stock}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to="/inventory"
                className="mx-4 mt-2 mb-2 flex items-center justify-between px-4 py-3 rounded-xl border border-[#F3F4F6] hover:border-[#121216] transition-colors"
              >
                <span className="text-[12px] font-medium text-[#4B5563]">Manage inventory</span>
                <ArrowUpRight size={13} weight="bold" className="text-[#121216]" />
              </Link>
            </div>
          </section>
        </div>

        {/* ============ UPCOMING BOOKINGS ============ */}
        <UpcomingBookings
          items={stats?.upcoming_bookings || []}
          count={stats?.upcoming_bookings_count ?? 0}
        />

        {/* ============ PIPELINE STRIP ============ */}
        <section>
          <div className="flex items-center gap-2 mb-4 px-1">
            <Clock size={14} weight="duotone" className="text-[#9CA3AF]" />
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              Repair pipeline
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(stats?.tickets_by_status || {}).map(([k, v]) => (
              <Link
                key={k}
                to={`/repairs?status=${k}`}
                data-testid={`pipeline-${k}`}
                className="group p-5 rounded-2xl bg-white border border-[#F3F4F6] hover:border-[#121216] transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLORS[k] }} />
                  <ArrowUpRight size={12} weight="bold" className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="font-display font-semibold text-[32px] leading-none text-[#121216] mt-6 tabular-nums">
                  {v}
                </div>
                <div className="text-[11px] text-[#4B5563] mt-2 font-medium">{STATUS_LABELS[k]}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <NewTicketWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={loadStats} />
    </Layout>
  );
}

function SnapStat({ label, value, testid, divider, trailing }) {
  return (
    <div className={`p-5 sm:p-6 lg:p-10 ${divider ? "border-t sm:border-t md:border-t lg:border-t-0 lg:border-l border-[#F3F4F6]" : "border-t sm:border-t md:border-t lg:border-t-0"}`}>
      <div className="text-[10px] sm:text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2 sm:mb-3 lg:mb-5">
        {label}
      </div>
      <div
        data-testid={testid}
        className="font-display text-[28px] sm:text-[36px] lg:text-[44px] leading-[1] tracking-[-0.02em] font-semibold text-[#121216] tabular-nums"
      >
        {value}
      </div>
      {trailing && <div className="mt-3 lg:mt-6">{trailing}</div>}
    </div>
  );
}

function StatusDot({ status }) {
  return (
    <span
      className="w-2 h-2 rounded-full shrink-0"
      style={{ background: STATUS_COLORS[status] }}
    />
  );
}

function StatusPill({ status }) {
  return (
    <span
      className="text-[10px] tracking-[0.06em] uppercase px-2.5 py-1 rounded-full font-semibold shrink-0"
      style={{
        background: `${STATUS_COLORS[status]}15`,
        color: STATUS_COLORS[status],
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// Upcoming bookings for the active store.
function UpcomingBookings({ items, count }) {
  const formatWhen = (iso) => {
    if (!iso) return "No time set";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const today = new Date();
    const tmr = new Date();
    tmr.setDate(today.getDate() + 1);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (sameDay(d, today)) return `Today · ${time}`;
    if (sameDay(d, tmr)) return `Tomorrow · ${time}`;
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) + ` · ${time}`;
  };

  return (
    <section
      className="rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden"
      style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02)" }}
      data-testid="upcoming-bookings"
    >
      <header className="px-7 pt-7 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <CalendarCheck size={16} weight="duotone" className="text-[#1E5FEB]" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              Upcoming bookings
            </div>
            <div className="mt-0.5 text-[18px] font-display font-semibold text-[#121216]">
              {count === 0 ? "No bookings" : `${count} booking${count === 1 ? "" : "s"} scheduled`}
            </div>
          </div>
        </div>
        <Link
          to="/bookings"
          className="text-[12px] text-[#4B5563] hover:text-[#121216] inline-flex items-center gap-1"
        >
          Manage bookings <ArrowUpRight size={12} weight="bold" />
        </Link>
      </header>

      <div className="px-3 pb-3">
        {items.length === 0 ? (
          <div className="mx-4 mb-4 px-5 py-10 rounded-2xl bg-[#F8FAFC] text-center">
            <div className="text-sm text-[#4B5563]">No upcoming bookings at this store.</div>
            <div className="text-[11px] text-[#9CA3AF] mt-1">
              Bookings from your website appear here and can be converted to a ticket.
            </div>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 px-2 pb-2">
            {items.map((b) => (
              <li
                key={b.id}
                className="group border border-[#F3F4F6] rounded-2xl p-4 hover:border-[#121216] transition-colors"
                data-testid={`upcoming-booking-${b.id}`}
              >
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[#121216] truncate">{b.customer_name}</div>
                    <div className="text-[11px] font-mono text-[#9CA3AF] flex items-center gap-1 mt-0.5">
                      {b.customer_phone && <><Phone size={10} weight="bold" />{b.customer_phone}</>}
                      {!b.customer_phone && <span>—</span>}
                    </div>
                  </div>
                  <span
                    className="text-[10px] tracking-[0.06em] uppercase px-2.5 py-1 rounded-full font-semibold shrink-0"
                    style={{
                      background: b.status === "confirmed" ? "#10B98115" : "#1E5FEB15",
                      color: b.status === "confirmed" ? "#10B981" : "#1E5FEB",
                    }}
                  >
                    {b.status}
                  </span>
                </div>
                <div className="text-[12px] text-[#4B5563] truncate">
                  {b.device_make || b.device_model ? `${b.device_make} ${b.device_model}`.trim() : "Device not specified"}
                  {b.problem ? ` — ${b.problem}` : ""}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-[11px] font-mono font-semibold text-[#1E5FEB]">
                    {formatWhen(b.preferred_at)}
                  </div>
                  <Link
                    to="/bookings"
                    className="text-[10px] font-bold tracking-[0.08em] text-[#121216] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1"
                  >
                    VIEW <ArrowUpRight size={10} weight="bold" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
